import { useEffect } from 'react';
import { GridApiPro } from '@mui/x-data-grid-pro';

const STORAGE_KEY_PREFIX = 'scroll-position:assets:';
const SAVE_DEBOUNCE_MS = 150;
const RESTORE_WINDOW_MS = 2000;

const subscribeToRestore = ({
  api,
  apiReference,
  key,
}: {
  api: GridApiPro;
  apiReference: React.RefObject<GridApiPro | null>;
  key: string;
}) => {
  const savedScrollTop = Number(sessionStorage.getItem(key));
  const hasPosition = Number.isFinite(savedScrollTop) && savedScrollTop > 0;

  if (!hasPosition) return () => {};

  let windowTimerId: ReturnType<typeof setTimeout> | undefined;
  let windowExpired = false;

  const unsubscribe = api.subscribeEvent('rowsSet', () => {
    if (windowExpired) return;

    clearTimeout(windowTimerId);
    windowTimerId = setTimeout(() => {
      windowExpired = true;
    }, RESTORE_WINDOW_MS);

    requestAnimationFrame(() => apiReference.current?.scroll({ top: savedScrollTop, left: 0 }));
  });

  return () => {
    clearTimeout(windowTimerId);
    unsubscribe();
  };
};

const subscribeToSave = ({ api, key }: { api: GridApiPro; key: string }) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  const unsubscribe = api.subscribeEvent('scrollPositionChange', (parameters: { top: number }) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => sessionStorage.setItem(key, String(parameters.top)), SAVE_DEBOUNCE_MS);
  });
  return () => {
    clearTimeout(timeoutId);
    unsubscribe();
  };
};

export const useScrollPositionRestore = ({
  apiReference,
  folderKey,
  shouldRestore = true,
}: {
  apiReference: React.RefObject<GridApiPro | null>;
  folderKey: string;
  shouldRestore?: boolean;
}) => {
  const storageKey = `${STORAGE_KEY_PREFIX}${folderKey}`;

  useEffect(() => {
    const api = apiReference.current;
    if (!api) return;

    const unsubscribeRestore = shouldRestore ? subscribeToRestore({ api, apiReference, key: storageKey }) : () => {};
    const unsubscribeSave = subscribeToSave({ api, key: storageKey });

    return () => {
      unsubscribeRestore();
      unsubscribeSave();
    };
  }, [apiReference, storageKey, shouldRestore]);
};
